import { type PagyMeta, PagyPagination } from "@/components/pagy-pagination"
import { Avatar, AvatarFallback, AvatarGroup, AvatarGroupCount, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import { IconDots, IconEye, IconPencil, IconPlus, IconSearch, IconTrash } from "@tabler/icons-react"
import { useState } from "react"

interface Employee {
  id: number
  name: string
  avatar_url: string | null
}

interface Customer {
  id: number
  name: string
  active: boolean
  projects_count: number
  employees: Employee[]
  created_at: string
}

interface StatusCounts {
  all: number
  active: number
  inactive: number
}

interface Props {
  [key: string]: unknown
  customers: Customer[]
  pagy: PagyMeta
  status_counts: StatusCounts
  filters: { search?: string; active?: string }
  auth: { is_admin: boolean }
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr)
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

export default function CustomersIndex() {
  const { customers, pagy, status_counts, filters, auth } = usePage<Props>().props

  const [search, setSearch] = useState(filters.search || "")

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get("/customers", { search: search || undefined }, { preserveState: true })
  }

  function handleFilter(active?: string) {
    router.get("/customers", { search: filters.search || undefined, active }, { preserveState: true })
  }

  function handleDelete(id: number) {
    if (confirm("Are you sure? This will also delete all associated projects and documents.")) {
      router.delete(`/customers/${id}`)
    }
  }

  const activeFilter = filters.active

  return (
    <>
      <Head title="Customers - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Customers</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Customers</h1>
          {auth.is_admin && (
            <Button size="sm" asChild>
              <a href="/customers/new">
                <IconPlus className="mr-1 h-4 w-4" />
                New Customer
              </a>
            </Button>
          )}
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <form onSubmit={handleSearch} className="flex gap-2 sm:max-w-sm">
            <div className="relative flex-1">
              <IconSearch className="text-muted-foreground absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search customers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button type="submit" variant="outline" size="default">
              Search
            </Button>
          </form>

          <div className="flex items-center gap-2">
            <span className="text-muted-foreground hidden text-sm sm:inline">Show:</span>
            {(
              [
                { label: "All", value: undefined, count: status_counts.all },
                { label: "Active", value: "true", count: status_counts.active },
                {
                  label: "Inactive",
                  value: "false",
                  count: status_counts.inactive,
                },
              ] as const
            ).map((filter) => (
              <Button
                key={filter.label}
                variant={activeFilter === filter.value ? "default" : "outline"}
                size="sm"
                onClick={() => handleFilter(filter.value)}
              >
                {filter.label} ({filter.count})
              </Button>
            ))}
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Projects</TableHead>
              <TableHead>Employees</TableHead>
              <TableHead>With Us Since</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {customers.map((customer) => (
              <TableRow key={customer.id}>
                <TableCell className="font-medium">#{customer.id}</TableCell>
                <TableCell className="font-medium">
                  <a href={`/customers/${customer.id}`} className="hover:underline">
                    {customer.name}
                  </a>
                </TableCell>
                <TableCell>
                  {customer.projects_count} {customer.projects_count === 1 ? "project" : "projects"}
                </TableCell>
                <TableCell>
                  {customer.employees.length > 0 ? (
                    <AvatarGroup>
                      {customer.employees.slice(0, 5).map((emp) => (
                        <Avatar key={emp.id} size="sm" title={emp.name}>
                          {emp.avatar_url ? <AvatarImage src={emp.avatar_url} alt={emp.name} /> : null}
                          <AvatarFallback>{emp.name.charAt(0).toUpperCase()}</AvatarFallback>
                        </Avatar>
                      ))}
                      {customer.employees.length > 5 && (
                        <AvatarGroupCount>+{customer.employees.length - 5}</AvatarGroupCount>
                      )}
                    </AvatarGroup>
                  ) : (
                    <span className="text-muted-foreground text-sm">No employees</span>
                  )}
                </TableCell>
                <TableCell>{formatDate(customer.created_at)}</TableCell>
                <TableCell>
                  <Badge variant={customer.active ? "default" : "destructive"}>
                    {customer.active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <IconDots className="h-4 w-4" />
                        <span className="sr-only">Actions</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <a href={`/customers/${customer.id}`}>
                          <IconEye className="mr-2 h-4 w-4" />
                          View
                        </a>
                      </DropdownMenuItem>
                      {auth.is_admin && (
                        <>
                          <DropdownMenuItem asChild>
                            <a href={`/customers/${customer.id}/edit`}>
                              <IconPencil className="mr-2 h-4 w-4" />
                              Edit
                            </a>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem variant="destructive" onClick={() => handleDelete(customer.id)}>
                            <IconTrash className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {customers.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground text-center">
                  No customers found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <PagyPagination pagy={pagy} />
      </div>
    </>
  )
}

CustomersIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
